import type { ClientToServerEvents, ServerToClientEvents } from '@point-ker/shared';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { io, type Socket } from 'socket.io-client';

import { clearToken, loadToken, saveToken } from '@/socket/session';

/**
 * Owns the single socket connection and the host session.
 *
 * The generics are reversed relative to the server: a client *listens* to
 * `ServerToClientEvents` and *emits* `ClientToServerEvents`. Getting this
 * backwards compiles fine but silently unties the contract, so it is spelled
 * out here once and reused everywhere.
 */
export type AppClientSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected';

interface SocketContextValue {
  readonly socket: AppClientSocket;
  readonly status: ConnectionStatus;
  /** True when a host token is held. The server still re-checks every action. */
  readonly isSignedIn: boolean;
  readonly signIn: (username: string, password: string) => Promise<void>;
  readonly signOut: () => void;
}

const SocketContext = createContext<SocketContextValue | null>(null);

/**
 * Empty in the single-dyno deploy, where the same origin serves the page, the
 * API and the WebSocket. Only set when the client is hosted separately.
 */
// Optional-chained because `import.meta.env` only exists under Vite; outside
// it (SSR, a plain Node harness) the module should fall back, not throw.
const SOCKET_URL = import.meta.env?.VITE_SOCKET_URL ?? '';

export function SocketProvider({ children }: { children: ReactNode }) {
  // Created once. `autoConnect: false` keeps the connection out of render so
  // the effect below owns its whole lifecycle.
  const [socket] = useState<AppClientSocket>(() =>
    io(SOCKET_URL, { autoConnect: false, auth: { token: loadToken() ?? undefined } }),
  );
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [token, setToken] = useState<string | null>(() => loadToken());

  useEffect(() => {
    const onConnect = () => setStatus('connected');
    const onDisconnect = () => setStatus('disconnected');
    const onError = () => setStatus('disconnected');

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('connect_error', onError);
    socket.connect();

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('connect_error', onError);
      socket.disconnect();
    };
  }, [socket]);

  const value = useMemo<SocketContextValue>(
    () => ({
      socket,
      status,
      isSignedIn: token !== null,
      signIn: async (username, password) => {
        const response = await fetch('/api/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, password }),
        });
        if (!response.ok) {
          const body: unknown = await response.json().catch(() => null);
          const message =
            typeof body === 'object' && body !== null && 'error' in body
              ? String((body as { error: unknown }).error)
              : 'Could not sign in.';
          throw new Error(message);
        }

        const { token: issued } = (await response.json()) as { token: string };
        saveToken(issued);
        setToken(issued);

        // The token only reaches the server in the handshake, so an existing
        // connection has to be re-established for it to count as a host.
        socket.auth = { token: issued };
        socket.disconnect().connect();
      },
      signOut: () => {
        clearToken();
        setToken(null);
        socket.auth = {};
        socket.disconnect().connect();
      },
    }),
    [socket, status, token],
  );

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}

export function useSocket(): SocketContextValue {
  const context = useContext(SocketContext);
  if (!context) throw new Error('useSocket must be used inside <SocketProvider>');
  return context;
}
